import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";

// Reelty-branded pin (oxblood teardrop) — inline SVG so we don't need any assets.
const REELTY_PIN = L.divIcon({
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="34" viewBox="0 0 28 34" style="filter: drop-shadow(0 3px 4px rgba(0,0,0,0.35))">
      <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 20 14 20s14-9.5 14-20C28 6.27 21.73 0 14 0z" fill="#6B0F1A"/>
      <circle cx="14" cy="14" r="5.5" fill="#F5EBD9"/>
    </svg>`,
  className: "reelty-pin",
  iconSize: [28, 34],
  iconAnchor: [14, 34],
});

// Miles → meters
const MILES_TO_M = 1609.344;

// Auto-fits the map view to the circle whenever coords or radius change.
function FitToCircle({ center, radiusMiles }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    // Choose a sensible zoom based on radius (leaflet needs a map projection to
    // call getBounds() on a bare Circle, so we derive the zoom heuristically).
    const km = radiusMiles * 1.60934;
    const z = km > 200 ? 6 : km > 100 ? 7 : km > 50 ? 8 : km > 25 ? 9 : km > 10 ? 10 : 11;
    map.setView(center, z, { animate: true });
  }, [center, radiusMiles, map]);
  return null;
}

export default function ServiceAreaMap({ zip, radiusMiles }) {
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    if (!zip || zip.length < 5) {
      setCoords(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/geo/zip/${zip.slice(0, 5)}`);
        if (!cancelled) setCoords([data.lat, data.lon]);
      } catch {
        if (!cancelled) {
          setCoords(null);
          setError("Couldn't locate that ZIP.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [zip]);

  if (!coords) {
    return (
      <div
        className="mt-4 rounded-sm border border-dashed border-[#1A0F0A]/20 bg-white/60 h-40 flex items-center justify-center text-center px-6"
        data-testid="service-area-map-placeholder"
      >
        <p className="font-mono-tag text-[10px] text-[#78584B] leading-relaxed">
          {error || "Enter a valid U.S. ZIP to see your coverage circle."}
        </p>
      </div>
    );
  }

  const radius = Number(radiusMiles) > 0 ? Number(radiusMiles) : 25;

  return (
    <div
      className="mt-4 rounded-sm overflow-hidden border border-[#1A0F0A]/12"
      data-testid="service-area-map"
      style={{ height: 220 }}
    >
      <MapContainer
        center={coords}
        zoom={9}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", background: "#F5EBD9" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Circle
          center={coords}
          radius={radius * MILES_TO_M}
          pathOptions={{
            color: "#6B0F1A",       // stroke 100% opacity oxblood
            weight: 2,
            fillColor: "#6B0F1A",   // fill oxblood
            fillOpacity: 0.18,      // faded
          }}
        />
        <Marker position={coords} icon={REELTY_PIN} />
        <FitToCircle center={coords} radiusMiles={radius} />
      </MapContainer>
    </div>
  );
}
